<script lang="ts" setup>
  import { computed, ref, type PropType, watch } from 'vue'
  import type { AIChatSession, CustomerUser as User } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import VueJsonPretty from 'vue-json-pretty'
  import 'vue-json-pretty/lib/styles.css'
  import { AiChatSessionsService } from '@/types/generated'

  const props = defineProps({
    activeChatSession: {
      type: Object as PropType<AIChatSession | null>,
      required: true
    },
    generatingSessionUuids: {
      type: Set as PropType<Set<string>>,
      required: true
    },
    currentUser: {
      type: Object as PropType<User>,
      required: true
    }
  })

  const route = useRoute()
  const isDebugMode = computed(() => route.query.debug === 'true')
  const showRawMessages = computed(() => isDebugMode.value)
  const systemMessage = ref(null)

  const lastResponseTime = ref<number | null>(null)
  const formattedResponseTime = computed(() => {
    if (lastResponseTime.value === null) return 'N/A'
    return `${lastResponseTime.value.toFixed(2)} seconds`
  })

  const fetchSystemMessage = async (sessionUuid: string) => {
    console.log('Fetching system message for session:', sessionUuid)
    const session = await AiChatSessionsService.aiChatSessionsRetrieve({
      uuid: sessionUuid
    })
    systemMessage.value = session.messages.find((m) => m.role === 'system')
    console.log('Got system message:', session)
  }

  // Watch for active session changes and fetch system message
  watch(
    () => props.activeChatSession?.uuid,
    async (newUuid) => {
      if (newUuid && isDebugMode.value) {
        await fetchSystemMessage(newUuid)
      } else {
        systemMessage.value = null
      }
    }
  )

  const startResponseTimer = () => {
    lastResponseTime.value = null
    responseStartTime = Date.now()
  }

  const endResponseTimer = () => {
    lastResponseTime.value = (Date.now() - responseStartTime) / 1000
  }

  let responseStartTime = 0

  defineExpose({
    startResponseTimer,
    endResponseTimer,
    showRawMessages
  })
</script>

<template>
  <div
    v-if="isDebugMode"
    class="fixed bottom-4 left-4 z-50 max-w-md rounded-lg bg-gray-800 p-4 text-white shadow-lg"
  >
    <h3 class="mb-2 text-sm font-bold">Debug Info</h3>
    <div class="mb-3 space-y-1 text-xs">
      <p>Active Session: {{ activeChatSession?.uuid }}</p>
      <p>Last response took: {{ formattedResponseTime }}</p>
      <p>Generating Sessions: {{ Array.from(generatingSessionUuids) }}</p>
      <p>User: {{ currentUser?.email }}</p>
    </div>

    <div
      v-if="systemMessage"
      class="mb-3"
    >
      <h4 class="mb-1 text-xs font-bold">System Message</h4>
      <div class="max-h-48 overflow-auto rounded-lg bg-gray-900 p-4">
        <VueJsonPretty
          :data="systemMessage.context"
          :deep="2"
          :show-double-quotes="false"
          :show-length="true"
          class="!bg-transparent"
          :showLine="false"
          :collapsed-on-click-brackets="true"
          :path-selectable="true"
        />
      </div>
    </div>

    <div class="flex items-center space-x-2">
      <button
        @click="showRawMessages = !showRawMessages"
        class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
        :class="showRawMessages ? 'bg-blue-600' : 'bg-gray-600'"
      >
        <span
          class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
          :class="showRawMessages ? 'translate-x-4' : 'translate-x-0.5'"
        />
      </button>
      <span class="text-xs">Show raw messages</span>
    </div>
  </div>
</template>

<style scoped>
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #4b5563;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #6b7280;
  }

  :deep(.vjs-tree) {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace !important;
    font-size: 0.75rem !important;
    line-height: 1rem !important;
    background-color: transparent !important;
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__string) {
    color: rgb(229 231 235) !important;
  }

  :deep(.vjs-tree .vjs-value__number) {
    color: rgb(129 140 248) !important;
  }

  :deep(.vjs-tree .vjs-value__boolean) {
    color: rgb(244 114 182) !important;
  }

  :deep(.vjs-tree .vjs-value__null) {
    color: rgb(248 113 113) !important;
  }

  :deep(.vjs-tree .vjs-key) {
    color: rgb(139 92 246) !important;
  }
</style>
